import {create} from 'zustand';
import {saveOrUpdate as saveOrUpdateApi} from '@/services/auditService/auditAdjustmentService';
import type {AuditAdjustmentHeader, AuditAdjustmentLine} from '@/services/auditService/auditAdjustmentService';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {formatDateTime} from '@/utils/dataUtils';
import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {effectiveLoanSum, resolveBalanceYoa} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanSummarySums';

export type StatementType = 'BS' | 'PL';

export interface AdjustmentEntry {
    statement: StatementType;
    code: string;
    fsId: number;
    // 通用金额字段（可按需扩展）
    adjustmentAmount?: number;
    currency?: string;
    // 追踪后端已有的 id，用于更新而非新增
    existingLineId?: number;
    existingDetailId?: number;
    // 动态组件序列化后的详情
    details: Record<string, any>;
}

interface AdjustmentState {
    activeHeaderId: string | null;
    // 設置當前激活的 Header Id (status = 0 的 草稿header)
    setActiveHeaderId: (id: string | null) => void;

    // 當前選擇的調整模式
    currentMode: AdjustmentMode | null;
    // 之前的调整模式（用于检测模式切换）
    previousMode: AdjustmentMode | null;
    setCurrentMode: (mode: AdjustmentMode | null) => void;

    // Mode 3 (ADJUST_WP_TO_FS) 辅助输入功能状态
    // 捕捉的第一个输入值
    mode3CapturedValue: number | null;
    // 捕捉的第一个输入的 fsId
    mode3CapturedFsId: number | null;
    // 捕捉的第一个输入的 entry 类型 ('Dr' | 'Cr')
    mode3CapturedEntry: string | null;
    // 辅助功能是否已使用（使用后不再辅助，直到 cancel 或重新加载）
    mode3AssistUsed: boolean;
    // 设置 mode3 捕捉的值
    setMode3CapturedValue: (value: number | null, fsId: number | null, entry: string | null) => void;
    // 标记 mode3 辅助已使用
    setMode3AssistUsed: (used: boolean) => void;
    // 清空 mode3 辅助状态
    clearMode3Assist: () => void;

    // Mode 2 (ADJUST_FS_TO_FS / FS_OPENING) 辅助输入功能状态
    // 捕捉的第一个输入值
    mode2CapturedValue: number | null;
    // 捕捉的第一个输入的 fsId
    mode2CapturedFsId: number | null;
    // 捕捉的第一个输入的 entry 类型 ('Dr' | 'Cr')
    mode2CapturedEntry: string | null;
    // 辅助功能是否已使用（使用后不再辅助，直到 cancel 或重新加载）
    mode2AssistUsed: boolean;
    // 设置 mode2 捕捉的值
    setMode2CapturedValue: (value: number | null, fsId: number | null, entry: string | null) => void;
    // 标记 mode2 辅助已使用
    setMode2AssistUsed: (used: boolean) => void;
    // 清空 mode2 辅助状态
    clearMode2Assist: () => void;
    // 清空 fsOpeningAmounts 的触发器（递增计数器，组件监听变化后清空自己的 state）
    fsOpeningResetTrigger: number;
    // 触发清空 fsOpeningAmounts
    triggerFsOpeningReset: () => void;
    // 初始化臨時 header（不調用後端）
    initTempHeader: (args: {
        companyId: number;
        annualId: number;
        adjustmentMode: AdjustmentMode;
        preparedUserId: number;
        existingHeaderId?: string | null;
    }) => void;

    // 全局“是否有未保存變更”的標記
    hasUpdated: boolean;
    setHasUpdated: (v: boolean) => void;

    // 以复合键做幂等管理
    entries: Record<string, AdjustmentEntry>;
    // 插入或更新一條entries中的條目。
    upsertEntry: (e: AdjustmentEntry) => void;
    // 批量插入/更新 entries
    bulkUpsert: (list: AdjustmentEntry[]) => void;
    // 移除指定 entry(key:string)
    removeEntry: (key: string) => void;
    // 清空所有 entries
    clearAll: () => void;
    // 清空特定的 line 数据
    clearLine: (code: string, fsId: number, statement: string) => void;
    // 根據 component（code）獲取當前 entries 中的 details.payload 列表
    getDetails: (component: string) => any[];

    // 在内存中暂存一个 Header（包含 lines），方便组件间协作
    header: AuditAdjustmentHeader | null;
    // 设置当前内存中的 header 对象（包含 lines），作为本地编辑“工作副本”。
    setHeader: (h: AuditAdjustmentHeader | null) => void;
    // 合并/更新某一行，按 lineId 以及明细 detailId 做匹配
    upsertHeaderLine: (line: AuditAdjustmentLine) => void;
    // 根据 code 和 annualId 获取 line
    getLine: (code: string, annualId: number) => AuditAdjustmentLine | null;

    // 获取所有 PPE 信息
    getAllPPEInfo: () => Record<string, Array<{
        date: string | null;
        item: string;
        additions: number | null;
        nbv?: number
    }>>;

    // 获取所有 Motor Hire 信息
    getAllMotorHire: () => Array<{
        date: string;
        item: string;
        cost: number;
        hpBf: number;
        downPayment: number;
        hpCf: number
    }>;

    // 获取所有 Gain/Loss Motor Disposal 信息
    getAllGainLossMotorDisposal: () => Array<{
        date: string;
        item: string;
        atCost: number;
        accumulated: number;
        sales: number;
        disposal: number
    }>;

    // 查询 PPE 各项的 addition 总和
    queryTotalAdditionPPE: () => Array<{
        item: string;
        cost: number;
    }>;

    // 获取全额支付车辆的 Gain/Loss 信息
    getFullPayVehicle: () => Array<{
        date: string;
        item: string;
        atCost: number;
        accumulated: number;
        sales: number;
        disposal: number
    }>;

    // 将 entries 聚合为后端的 lines
    buildLines: (
        companyId: number,
        annualId: number,
        scheduleNameMap?: Map<string, string>,
        existingLines?: AuditAdjustmentLine[],
        headerId?: string | null,
    ) => AuditAdjustmentLine[];

    // 2.1 根據 headerId 調用 saveOrUpdate 進行保存與更新
    saveOrUpdateByHeaderId: (args: {
        headerId: string;
        companyId: number;
        annualId: number;
        scheduleNameMap?: Map<string, string>;
    }) => Promise<AuditAdjustmentHeader>;
}

function makeKey(e: { statement: StatementType; code: string; fsId: number }): string {
    return `${e.statement}|${e.code}|${e.fsId}`;
}

function normalizeLineCode(input: any): string {
    const raw = String(input || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    if (upper.startsWith('BS-') || upper.startsWith('PL-') || upper.startsWith('FS-')) return raw;
    const mapped = useFsAdjustStore.getState().getLineByCode(raw)?.code;
    const mappedUpper = String(mapped || '').trim().toUpperCase();
    if (mappedUpper.startsWith('BS-') || mappedUpper.startsWith('PL-') || mappedUpper.startsWith('FS-')) return mapped as string;
    return raw;
}

function getBackendLineCode(line: any): string {
    return normalizeLineCode((line as any)?.code || (line as any)?.scheduleCode);
}

function matchesAnyCode(actual: any, candidates: string[]): boolean {
    const a = String(actual || '').trim().toUpperCase();
    if (!a) return false;
    return candidates.some((c) => String(c || '').trim().toUpperCase() === a);
}

export const useAdjustmentStore = create<AdjustmentState>((set, get) => ({
    activeHeaderId: null,
    setActiveHeaderId: (id) => set({activeHeaderId: (typeof id === 'string' && id.trim()) ? id : null}),

    currentMode: null,
    previousMode: null,
    setCurrentMode: (mode) => set((state) => ({ 
        currentMode: mode,
        previousMode: state.currentMode  // 保存之前的模式
    })),

    // Mode 3 辅助输入功能状态
    mode3CapturedValue: null,
    mode3CapturedFsId: null,
    mode3CapturedEntry: null,
    mode3AssistUsed: false,
    setMode3CapturedValue: (value, fsId, entry) => set({
        mode3CapturedValue: value,
        mode3CapturedFsId: fsId,
        mode3CapturedEntry: entry,
    }),
    setMode3AssistUsed: (used) => set({ mode3AssistUsed: used }),
    clearMode3Assist: () => set({
        mode3CapturedValue: null,
        mode3CapturedFsId: null,
        mode3CapturedEntry: null,
        mode3AssistUsed: false,
    }),

    // Mode 2 (FS_OPENING) 辅助输入功能状态
    mode2CapturedValue: null,
    mode2CapturedFsId: null,
    mode2CapturedEntry: null,
    mode2AssistUsed: false,
    setMode2CapturedValue: (value, fsId, entry) => set({
        mode2CapturedValue: value,
        mode2CapturedFsId: fsId,
        mode2CapturedEntry: entry,
    }),
    setMode2AssistUsed: (used) => set({ mode2AssistUsed: used }),
    clearMode2Assist: () => set({
        mode2CapturedValue: null,
        mode2CapturedFsId: null,
        mode2CapturedEntry: null,
        mode2AssistUsed: false,
    }),
    // 清空 fsOpeningAmounts 的触发器
    fsOpeningResetTrigger: 0,
    triggerFsOpeningReset: () => set((state) => ({ fsOpeningResetTrigger: state.fsOpeningResetTrigger + 1 })),
    initTempHeader: ({companyId, annualId, adjustmentMode, preparedUserId, existingHeaderId}) => {
        const state = get();
        const currentHeader = state.header;
        
        // ✅ 不要默认设置 header id，除非明确传入了 existingHeaderId
        // 或者是同一个模式下的更新操作
        let headerIdToUse: string | undefined = undefined;
        
        // 只有明确传入 existingHeaderId 或者是同模式更新时才使用 id
        if (existingHeaderId) {
            // 明确指定使用某个 header id
            headerIdToUse = existingHeaderId;
        } else if (currentHeader?.id && (currentHeader as any)?.adjustmentMode === adjustmentMode) {
            // 同一模式下的更新操作
            headerIdToUse = currentHeader.id;
        }
        
        const tempHeader: AuditAdjustmentHeader = {
            id: headerIdToUse,  // 只在需要更新时才设置
            adjustmentMode,
            companyId,
            annualId,
            refNo: headerIdToUse ? ((currentHeader as any)?.refNo || '') : '',
            status: 0,  // 始终使用草稿状态
            preparedUserId,
            preparedDate: formatDateTime(),
            reviewedUserId: 0,  // 新建时不设置
            reviewedDate: '',  // 新建时不设置
            lines: headerIdToUse ? ((currentHeader as any)?.lines || []) : [],  // 只在更新时保留现有 lines
        };
        
        set((state) => ({
            header: tempHeader, 
            currentMode: adjustmentMode,
            previousMode: state.currentMode
        }));
    },

    hasUpdated: false,
    setHasUpdated: (v) => set({hasUpdated: !!v}),

    entries: {},
    upsertEntry: (e) => set((s) => ({
        entries: {
            ...s.entries,
            [makeKey(e)]: e,
        },
        hasUpdated: true,
    })),
    bulkUpsert: (list) => set((s) => {
        const next = {...s.entries} as Record<string, AdjustmentEntry>;
        for (const e of list || []) {
            if (!e || !e.code || !e.fsId || !e.statement) continue;
            next[makeKey(e)] = e;
        }
        return {entries: next, hasUpdated: false};
    }),
    removeEntry: (key) => set((s) => {
        const n = {...s.entries};
        delete n[key];
        return {entries: n, hasUpdated: true};
    }),
    clearAll: () => set({entries: {}, currentMode: null, previousMode: null}),
    clearLine: (code, fsId, statement) => set((s) => {
        // 清空特定 line 的 entry
        const key = `${statement}|${code}|${fsId}`;
        const newEntries = {...s.entries};
        delete newEntries[key];
        
        // 同时清空 header 中对应的 line
        // 注意：line.scheduleCode 是后端数据字段，保持不变
        if (s.header && (s.header as any).lines) {
            const lines = [...((s.header as any).lines || [])];
            const targetCode = normalizeLineCode(code);
            const filteredLines = lines.filter((line: any) => 
                !(getBackendLineCode(line) === targetCode && 
                  line.fsId === fsId && 
                  line.statement === statement)
            );
            return {
                entries: newEntries,
                header: {...s.header, lines: filteredLines} as AuditAdjustmentHeader
            };
        }
        
        return {entries: newEntries};
    }),
    getDetails: (component) => {
        const comp = String(component || '').trim().toUpperCase();
        if (!comp) return [];
        const out: any[] = [];
        const entries = get().entries || {};
        for (const e of Object.values(entries)) {
            const d = (e as any)?.details;
            // Since details is now Record<string, any>, we use the details directly
            if (d && typeof d === 'object') {
                out.push(d);
            }
        }
        return out;
    },

    header: null,
    setHeader: (h) => set({header: h}),
    upsertHeaderLine: (line) => set((s) => {
        // 若没有 header，初始化一个最小的 header
        const current = s.header ? {...s.header} as AuditAdjustmentHeader : ({
            id: (line as any)?.headerId || get().activeHeaderId || null,
            companyId: (line as any)?.companyId || 0,
            annualId: (line as any)?.annualId || 0,
            adjustmentMode: (s.header as any)?.adjustmentMode || 0,
            refNo: (s.header as any)?.refNo || '',
            status: (s.header as any)?.status || 0,
            preparedUserId: (s.header as any)?.preparedUserId || 0,
            preparedDate: (s.header as any)?.preparedDate || '',
            reviewedUserId: (s.header as any)?.reviewedUserId || 0,
            reviewedDate: (s.header as any)?.reviewedDate || '',
            lines: [] as any[],
        } as unknown as AuditAdjustmentHeader);

        const lines: any[] = Array.isArray((current as any).lines) ? [...(current as any).lines] : [];
        const incoming: any = {...line} as any;
        const incomingId = Number(incoming?.id || incoming?.lineId || 0) || 0;
        const idx = lines.findIndex((ln: any) => Number(ln?.id || ln?.lineId || 0) === incomingId && incomingId > 0);
        if (idx >= 0) {
            // Found by id. Check scheduleCode consistency.
            const baseExisting = lines[idx] as any;
            const existingCode = getBackendLineCode(baseExisting);
            const incomingCode = getBackendLineCode(incoming);

            if (existingCode && incomingCode && existingCode !== incomingCode) {
                // scheduleCode changed -> create a new line for new scheduleCode
                const newIncoming: any = {...incoming};
                // ensure new creation
                newIncoming.id = null;
                if ('lineId' in newIncoming) delete newIncoming.lineId;
                // details keep as provided
                lines.push(newIncoming);
            } else {
                // merge line-level scalar fields
                const base = {...baseExisting} as any;
                const merged = {
                    ...base,
                    ...incoming,
                    id: Number(base.id || incoming.id || 0) || undefined,
                    lineId: Number(base.lineId || incoming.lineId || base.id || incoming.id || 0) || undefined,
                } as any;
                // merge details by detail id
                const baseDetails: any[] = Array.isArray(base.details) ? [...base.details] : [];
                const incomingDetails: any[] = Array.isArray(incoming.details) ? incoming.details : [];
                for (const d of incomingDetails) {
                    const did = Number(d?.id || d?.detailId || 0) || 0;
                    const j = baseDetails.findIndex((x: any) => Number(x?.id || x?.detailId || 0) === did && did > 0);
                    if (j >= 0) {
                        baseDetails[j] = {...baseDetails[j], ...d};
                    } else {
                        baseDetails.push({...d});
                    }
                }
                merged.details = baseDetails;
                lines[idx] = merged;
            }
        } else {
            // Not found by id. Try merge by scheduleCode, otherwise append.
            const incomingCode = getBackendLineCode(incoming);
            const scIdx = incomingCode ? lines.findIndex((ln: any) => getBackendLineCode(ln) === incomingCode) : -1;
            if (scIdx >= 0) {
                const base = {...lines[scIdx]} as any;
                const merged = {
                    ...base,
                    ...incoming,
                    id: Number(base.id || incoming.id || 0) || undefined,
                    lineId: Number(base.lineId || incoming.lineId || base.id || incoming.id || 0) || undefined,
                } as any;
                const baseDetails: any[] = Array.isArray(base.details) ? [...base.details] : [];
                const incomingDetails: any[] = Array.isArray(incoming.details) ? incoming.details : [];
                for (const d of incomingDetails) {
                    const did = Number(d?.id || d?.detailId || 0) || 0;
                    const j = baseDetails.findIndex((x: any) => Number(x?.id || x?.detailId || 0) === did && did > 0);
                    if (j >= 0) {
                        baseDetails[j] = {...baseDetails[j], ...d};
                    } else {
                        baseDetails.push({...d});
                    }
                }
                merged.details = baseDetails;
                lines[scIdx] = merged;
            } else {
                // new line
                lines.push(incoming);
            }
        }

        (current as any).lines = lines;
        return {header: current};
    }),

    getLine: (code, annualId) => {
        const state = get();
        const header = state.header;

        // 如果没有 header 或 lines，返回 null
        if (!header || !(header as any).lines || !Array.isArray((header as any).lines)) {
            return null;
        }

        const lines = (header as any).lines as AuditAdjustmentLine[];
        const targetCode = normalizeLineCode(code);

        // 查找匹配 code 和 annualId 的 line
        // 注意：line.scheduleCode 是后端数据字段
        const matchedLine = lines.find((line: any) =>
            getBackendLineCode(line) === targetCode && line.annualId === annualId
        );

        return matchedLine || null;
    },

    getAllPPEInfo: () => {
        const state = get();
        const entries = state.entries || {};
        const header = state.header;
        const result: Record<string, Array<{
            date: string | null;
            item: string;
            additions: number | null;
            nbv?: number
        }>> = {};

        // Helper function to extract PPE data from payload
        const extractPPEData = (payload: any) => {
            if (!payload) return;

            // Define the row keys we want to extract
            const rowKeys = [
                'locationOfPropertyRow',
                'commercialAllowanceRow',
                'industrialAllowanceRow',
                'furnitureFixtureRow',
                'computerHardSoftRow',
                'fullPayMotorRow',
                'hirePurchaseMotorRow',
                'machineryRow',
                'liftEscalatorRow',
                'equipmentRow'
            ];

            for (const key of rowKeys) {
                const rows = payload[key];
                if (Array.isArray(rows)) {
                    // Map the key to the output format (remove 'Row' suffix)
                    const outputKey = key.replace('Row', '');

                    if (!result[outputKey]) {
                        result[outputKey] = [];
                    }

                    // Extract date, item, additions and calculate nbv from each row
                    for (const row of rows) {
                        const costBf = Number(row.costBf ?? 0);
                        const additions = Number(row.additions ?? 0);
                        const accDepBf = Number(row.accDepBf ?? 0);
                        const depnChgFinal = Number(row.depnChgFinal ?? 0);

                        // Calculate nbv: costBf + additions - accDepBf - depnChgFinal
                        const nbv = costBf + additions - accDepBf - depnChgFinal;

                        result[outputKey].push({
                            date: row.date ?? null,
                            item: row.item ?? '',
                            additions: row.additions ?? null,
                            nbv: nbv,
                        });
                    }
                }
            }
        };

        // First, check entries (legacy storage)
        for (const entry of Object.values(entries)) {
            const details = (entry as any)?.details;
            // Since details is now Record<string, any>, we use the details directly
            if (details && typeof details === 'object') {
                extractPPEData(details);
            }
        }

        // Second, check header.lines (new storage mechanism)
        if (header && (header as any).lines && Array.isArray((header as any).lines)) {
            const lines = (header as any).lines as AuditAdjustmentLine[];

            for (const line of lines) {
                if (line.details && Array.isArray(line.details)) {
                    for (const detail of line.details) {
                        const component = String((detail as any)?.component || '').trim().toUpperCase();

                        if (component === 'PROPERTY_PLANT_EQUIPMENT') {
                            const payload = (detail as any)?.payload;
                            extractPPEData(payload);
                        }
                    }
                }
            }
        }

        return result;
    },

    getAllMotorHire: () => {
        const state = get();
        const header = state.header;
        const result: Array<{
            date: string;
            item: string;
            cost: number;
            hpBf: number;
            downPayment: number;
            hpCf: number
        }> = [];

        // Check header.lines for VEHICLE_LOAN data
        if (header && (header as any).lines && Array.isArray((header as any).lines)) {
            const lines = (header as any).lines as AuditAdjustmentLine[];

            for (const line of lines) {
                const lineCode = getBackendLineCode(line);
                if (matchesAnyCode(lineCode, ['VEHICLE_LOAN', 'BS-CL-OFL', 'BS-NCL-OFL'])) {
                    if (line.details && Array.isArray(line.details)) {
                        for (const detail of line.details) {
                            const component = String((detail as any)?.component || '').trim().toUpperCase();

                            if (component === 'VEHICLE_LOAN' || component === 'BS-CL-OFL' || component === 'BS-NCL-OFL') {
                                const payload = (detail as any)?.payload;

                                // Extract vehicle loan data
                                if (payload && payload.vehicleLoans && Array.isArray(payload.vehicleLoans)) {
                                    for (const vehicle of payload.vehicleLoans) {
                                        // WHY: hpBf = lastYoa Balance (Change ?? base); repayments = hpBf − yoa Balance; hpCf = hpBf − repayments
                                        const hpBf = effectiveLoanSum(
                                            vehicle.sums?.balanceLastYoa,
                                            vehicle.sums?.balanceLastYoaChange,
                                        );
                                        const repaymentsForPeriod = hpBf - resolveBalanceYoa(vehicle.sums);
                                        result.push({
                                            date: vehicle.firstRepayment || '',
                                            item: vehicle.mv || '',
                                            cost: Number(vehicle.purchaseCost || 0),
                                            hpBf,
                                            // WHY: P1.21 DownPayment returned signed negative
                                            downPayment: -Math.abs(Number(vehicle.downPayment || 0)),
                                            hpCf: hpBf - repaymentsForPeriod,
                                        });
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return result;
    },

    getAllGainLossMotorDisposal: () => {
        const state = get();
        const header = state.header;
        const result: Array<{
            date: string;
            item: string;
            atCost: number;
            accumulated: number;
            sales: number;
            disposal: number
        }> = [];

        // Maps to store hirePurchaseMotorRow data by item key
        const hirePurchaseMotorMap = new Map<string, {
            date: string;
            item: string;
            atCost: number;
            accumulated: number
        }>();

        // Check header.lines for PROPERTY_PLANT_EQUIPMENT data
        if (header && (header as any).lines && Array.isArray((header as any).lines)) {
            const lines = (header as any).lines as AuditAdjustmentLine[];

            for (const line of lines) {
                const scheduleCode = String(getBackendLineCode(line) || '').trim().toUpperCase();

                // Extract hirePurchaseMotorRow from PROPERTY_PLANT_EQUIPMENT
                if (matchesAnyCode(scheduleCode, ['PROPERTY_PLANT_EQUIPMENT', 'BS-NCA-PPE'])) {
                    if (line.details && Array.isArray(line.details)) {
                        for (const detail of line.details) {
                            const component = String((detail as any)?.component || '').trim().toUpperCase();

                            if (matchesAnyCode(component, ['PROPERTY_PLANT_EQUIPMENT', 'BS-NCA-PPE'])) {
                                const payload = (detail as any)?.payload;

                                if (payload && payload.hirePurchaseMotorRow && Array.isArray(payload.hirePurchaseMotorRow)) {
                                    for (const row of payload.hirePurchaseMotorRow) {
                                        const costBf = Number(row.costBf || 0);
                                        const additions = Number(row.additions || 0);
                                        const accDepBf = Number(row.accDepBf || 0);
                                        const depnChgFinal = Number(row.depnChgFinal || 0);

                                        const key = row.item || '';
                                        hirePurchaseMotorMap.set(key, {
                                            date: row.date || '',
                                            item: row.item || '',
                                            atCost: costBf + additions,
                                            accumulated: accDepBf + depnChgFinal,
                                        });
                                    }
                                }
                            }
                        }
                    }
                }

                // Extract hirePurchaseMotorDisRow from GAIN_LOSS_DISPOSAL
                if (matchesAnyCode(scheduleCode, ['GAIN_LOSS_DISPOSAL', 'PL-OI-GOD', 'PL-AE-LOD'])) {
                    if (line.details && Array.isArray(line.details)) {
                        for (const detail of line.details) {
                            const component = String((detail as any)?.component || '').trim().toUpperCase();

                            if (matchesAnyCode(component, ['GAIN_LOSS_DISPOSAL', 'PL-OI-GOD', 'PL-AE-LOD'])) {
                                const payload = (detail as any)?.payload;

                                if (payload && payload.hirePurchaseMotorDisRow && Array.isArray(payload.hirePurchaseMotorDisRow)) {
                                    for (const row of payload.hirePurchaseMotorDisRow) {
                                        const item = row.item || '';
                                        const ppeData = hirePurchaseMotorMap.get(item);

                                        if (ppeData) {
                                            result.push({
                                                date: ppeData.date,
                                                item: ppeData.item,
                                                atCost: ppeData.atCost,
                                                accumulated: ppeData.accumulated,
                                                sales: Number(row.salesProceed || 0),
                                                disposal: Number(row.perClient || 0),
                                            });
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return result;
    },

    queryTotalAdditionPPE: () => {
        const state = get();
        const header = state.header;
        const result: Array<{ item: string; cost: number }> = [];

        // Define mapping of row keys to display names
        const rowMappings = [
            { key: 'liftEscalatorRow', item: 'Lifts and escalators (electric)' },
            { key: 'furnitureFixtureRow', item: 'Furniture and fixture' },
            { key: 'computerHardSoftRow', item: 'Computer hardware and software' },
            { key: 'fullPayMotorRow', item: 'Motor Vehicle' },
            { key: 'machineryRow', item: 'Machineries' },
            { key: 'equipmentRow', item: 'Equipments' },
        ];

        // Helper function to calculate total additions for a specific row type
        const calculateTotalAdditions = (payload: any, rowKey: string): number => {
            if (!payload || !payload[rowKey] || !Array.isArray(payload[rowKey])) {
                return 0;
            }

            return payload[rowKey].reduce((sum: number, row: any) => {
                const additions = Number(row.additions || 0);
                return sum + additions;
            }, 0);
        };

        // Check header.lines for PROPERTY_PLANT_EQUIPMENT data
        if (header && (header as any).lines && Array.isArray((header as any).lines)) {
            const lines = (header as any).lines as AuditAdjustmentLine[];

            for (const line of lines) {
                const scheduleCode = String(getBackendLineCode(line) || '').trim().toUpperCase();

                if (matchesAnyCode(scheduleCode, ['PROPERTY_PLANT_EQUIPMENT', 'BS-NCA-PPE'])) {
                    if (line.details && Array.isArray(line.details)) {
                        for (const detail of line.details) {
                            const component = String((detail as any)?.component || '').trim().toUpperCase();

                            if (matchesAnyCode(component, ['PROPERTY_PLANT_EQUIPMENT', 'BS-NCA-PPE'])) {
                                const payload = (detail as any)?.payload;

                                // Calculate totals for each row type
                                for (const mapping of rowMappings) {
                                    const cost = calculateTotalAdditions(payload, mapping.key);
                                    result.push({
                                        item: mapping.item,
                                        cost: cost,
                                    });
                                }
                            }
                        }
                    }
                }
            }
        }

        return result;
    },

    getFullPayVehicle: () => {
        const state = get();
        const header = state.header;
        const result: Array<{
            date: string;
            item: string;
            atCost: number;
            accumulated: number;
            sales: number;
            disposal: number
        }> = [];

        // Maps to store fullPayMotorRow data by item key
        const fullPayMotorMap = new Map<string, {
            date: string;
            item: string;
            atCost: number;
            accumulated: number
        }>();

        // Check header.lines for PROPERTY_PLANT_EQUIPMENT data
        if (header && (header as any).lines && Array.isArray((header as any).lines)) {
            const lines = (header as any).lines as AuditAdjustmentLine[];

            for (const line of lines) {
                const scheduleCode = String(getBackendLineCode(line) || '').trim().toUpperCase();

                // Extract fullPayMotorRow from PROPERTY_PLANT_EQUIPMENT
                if (matchesAnyCode(scheduleCode, ['PROPERTY_PLANT_EQUIPMENT', 'BS-NCA-PPE'])) {
                    if (line.details && Array.isArray(line.details)) {
                        for (const detail of line.details) {
                            const component = String((detail as any)?.component || '').trim().toUpperCase();

                            if (matchesAnyCode(component, ['PROPERTY_PLANT_EQUIPMENT', 'BS-NCA-PPE'])) {
                                const payload = (detail as any)?.payload;

                                if (payload && payload.fullPayMotorRow && Array.isArray(payload.fullPayMotorRow)) {
                                    for (const row of payload.fullPayMotorRow) {
                                        const costBf = Number(row.costBf || 0);
                                        const additions = Number(row.additions || 0);
                                        const accDepBf = Number(row.accDepBf || 0);
                                        const depnChgFinal = Number(row.depnChgFinal || 0);

                                        const key = row.item || '';
                                        fullPayMotorMap.set(key, {
                                            date: row.date || '',
                                            item: row.item || '',
                                            atCost: costBf + additions,
                                            accumulated: accDepBf + depnChgFinal,
                                        });
                                    }
                                }
                            }
                        }
                    }
                }

                // Extract hirePurchaseMotorDisRow from GAIN_LOSS_DISPOSAL
                if (matchesAnyCode(scheduleCode, ['GAIN_LOSS_DISPOSAL', 'PL-OI-GOD', 'PL-AE-LOD'])) {
                    if (line.details && Array.isArray(line.details)) {
                        for (const detail of line.details) {
                            const component = String((detail as any)?.component || '').trim().toUpperCase();

                            if (matchesAnyCode(component, ['GAIN_LOSS_DISPOSAL', 'PL-OI-GOD', 'PL-AE-LOD'])) {
                                const payload = (detail as any)?.payload;

                                if (payload && payload.hirePurchaseMotorDisRow && Array.isArray(payload.hirePurchaseMotorDisRow)) {
                                    for (const row of payload.hirePurchaseMotorDisRow) {
                                        const item = row.item || '';
                                        const ppeData = fullPayMotorMap.get(item);

                                        if (ppeData) {
                                            result.push({
                                                date: ppeData.date,
                                                item: ppeData.item,
                                                atCost: ppeData.atCost,
                                                accumulated: ppeData.accumulated,
                                                sales: Number(row.salesProceed || 0),
                                                disposal: Number(row.perClient || 0),
                                            });
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return result;
    },

    buildLines: (companyId, annualId, scheduleNameMap, existingLines = [], headerId) => {
        const out: AuditAdjustmentLine[] = [];
        const entries = get().entries;

        // 先把现有 lines 建索引（按 statement|scheduleCode|fsId）
        const lineKey = (ln: Partial<AuditAdjustmentLine>) => `${String((ln as any).statement || '')}|${normalizeLineCode((ln as any).code || (ln as any).scheduleCode)}|${Number((ln as any).fsId || 0)}`;
        const existMap = new Map<string, AuditAdjustmentLine>();
        for (const ln of existingLines || []) existMap.set(lineKey(ln), ln);

        // 准备一个可变的合并副本（以 line 为单位）
        const mergedMap = new Map<string, AuditAdjustmentLine>();
        for (const [k, ln] of existMap.entries()) {
            const details = Array.isArray((ln as any)?.details) ? (ln as any).details : [];
            // ✅ 只有当后端返回的 line 有 id 时才保留
            const lineId = (ln as any).id && typeof (ln as any).id === 'number' ? (ln as any).id : undefined;
            const normalizedCode = normalizeLineCode((ln as any).code || (ln as any).scheduleCode);
            mergedMap.set(k, {
                id: lineId,  // 只保留后端返回的 id
                headerId: headerId ?? undefined,
                companyId,
                annualId,
                fsId: Number((ln as any).fsId || 0),
                statement: String((ln as any).statement || ''),
                scheduleCode: String((ln as any).scheduleCode || ''),
                scheduleName: (ln as any).scheduleName || scheduleNameMap?.get(normalizedCode) || undefined,
                extraItemName: (ln as any).extraItemName,
                currency: (ln as any).currency,
                openingAmount: (ln as any).openingAmount,
                adjustmentAmount: (ln as any).adjustmentAmount,
                closingAmount: (ln as any).closingAmount,
                details: details.map((d: any) => {
                    const dd: any = {...d};
                    // ✅ 只保留后端返回的 detail id
                    // 不要自动添加 detailId 别名
                    return dd;
                }),
            });
        }

        // 把 entries 覆盖/追加进 mergedMap
        for (const e of Object.values(entries)) {
            const k = `${e.statement}|${e.code}|${e.fsId}`;
            const existing = mergedMap.get(k);
            const normalizedCode = normalizeLineCode(e.code);
            const base: AuditAdjustmentLine = existing ?? {
                // ✅ 新建 line 时不设置 id
                // id: undefined,  // 不设置 id
                headerId: headerId ?? undefined,
                companyId,
                annualId,
                fsId: e.fsId,
                statement: e.statement,
                scheduleCode: e.code,  // 后端使用 scheduleCode
                scheduleName: scheduleNameMap?.get(normalizedCode) || undefined,
                currency: e.currency,
                details: [],
            };
            // ✅ 只有当后端已经存在该 line 且有 id 时才保留
            // 不要从 entry.existingLineId 获取
            // merge/replace component-level detail
            // Wrap the frontend details in backend format since backend expects {component, version, payload}
            const nextDetail: any = {
                component: e.code,  // 后端使用 component
                version: 1,
                payload: {...e.details}
            };
            
            // ✅ 不要默认设置 detail id
            // 只有当从现有 details 中找到对应的 detail 且有 id 时才保留
            const existingDetail = (base.details as any[]).find((d: any) => 
                d.component === e.code
            );
            if (existingDetail?.id && typeof existingDetail.id === 'number') {
                nextDetail.id = existingDetail.id;
            }
            
            // 更新或添加 detail
            const existingIdx = (base.details as any[]).findIndex((d: any) => 
                d.component === e.code
            );
            if (existingIdx >= 0) {
                (base.details as any[])[existingIdx] = nextDetail;
            } else {
                (base.details as any[]).push(nextDetail);
            }
            // 金额等字段也可以按需合并（这里简单覆盖/赋值）
            base.adjustmentAmount = e.adjustmentAmount;
            base.currency = e.currency ?? base.currency;
            mergedMap.set(k, base);
        }

        mergedMap.forEach((v) => out.push(v));
        // ✅ 不要自动添加 lineId 别名，只使用 id 字段
        return out;
    },

    saveOrUpdateByHeaderId: async ({headerId, companyId, annualId, scheduleNameMap}) => {
        if (!headerId) throw new Error('missing headerId');
        // 取當前內存中的 header 作為已有行的來源（若匹配 headerId）
        const s = get();
        const baseHeader = (s.header && String((s.header as any).id ?? '') === String(headerId)) ? s.header : null;
        const existingLines = (baseHeader?.lines as any[]) || [];
        const lines = s.buildLines(companyId, annualId, scheduleNameMap, existingLines as any, headerId);
        const body: AuditAdjustmentHeader = {
            // 最小必要字段，後端將以 id 識別 header
            id: headerId,
            companyId,
            annualId,
            // 其餘字段若已存在於 baseHeader，盡量沿用以提升兼容性
            adjustmentMode: (baseHeader as any)?.adjustmentMode ?? 0,
            refNo: (baseHeader as any)?.refNo ?? '',
            status: (baseHeader as any)?.status ?? 0,
            preparedUserId: (baseHeader as any)?.preparedUserId ?? 0,
            preparedDate: (baseHeader as any)?.preparedDate ?? '',
            reviewedUserId: (baseHeader as any)?.reviewedUserId ?? 0,
            reviewedDate: (baseHeader as any)?.reviewedDate ?? '',
            lines: lines as any,
        } as AuditAdjustmentHeader;
        const resp = await saveOrUpdateApi(body);
        // 更新內存中的 header，確保後續 merge 更準確
        try {
            set({header: resp, hasUpdated: false});
        } catch {
        }
        return resp as AuditAdjustmentHeader;
    },
}));
